import {
  RA_FINDING_ACTION_STATUSES,
  RA_FINDING_TYPES,
  type RaFindingDecision,
  type RaFindingDecisionKind,
  type RaFindingEvent,
  type RaFindingType,
} from '@felix/domain';
import { Button, Checkbox, Modal, Radio, Select, Textarea, TextInput } from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconBuildingFactory2,
  IconClockExclamation,
  IconFlag,
  IconHandStop,
  IconPencil,
  IconPlus,
  IconSearch,
  IconTrash,
} from '@tabler/icons-react';
import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../../EmptyState.js';
import { formatDateTime, formatDay } from '../../../format.js';
import { DayInput } from '../../../inputs/DayInput.js';
import { offsetLabel } from '../../../inputs/lt-utc.js';
import { PageHeader } from '../../../PageHeader.js';
import { Panel } from '../../../Panel.js';
import { StatTile } from '../../../StatTile.js';
import { StatusBadge } from '../../../StatusBadge.js';
import { useShell } from '../../../shell-context.js';
import { defectsPath } from '../../defects/routes.js';
import { hsePath } from '../../hse/routes.js';
import table from '../../materials/Materials.module.css';
import { errorCode } from '../../personnel/source.js';
import { OwnerAttachments } from '../../pms/OwnerAttachments.js';
import purchasing from '../../purchasing/Purchasing.module.css';
import { LoadError, NoMatch, TablePager, TableSkeleton } from '../../purchasing/table.js';
import { ReturnedBanner } from '../../returned/Returned.js';
import library from '../library/RaLibrary.module.css';
import master from '../master/RaMaster.module.css';
import { ActionDialog } from '../RiskParts.js';
import classes from '../verification/RaVerification.module.css';
import { RA_VERIFICATION_PATH } from '../verification/RaVerificationScreen.js';
import type {
  RaFindingActionInput,
  RaFindingDetail,
  RaFindingFields,
  RaFindingListView,
  RaFindingSource,
  RaFindingSummary,
} from './types.js';
import {
  actionDraft,
  actionIssues,
  EMPTY_ACTION,
  EMPTY_FINDING_FILTER,
  FINDING_FILTERS,
  type FindingListFilter,
  filterFindings,
  findingStats,
  findingTone,
  type RaFindingsRoute,
  raFindingsPath,
  requestTone,
} from './view.js';

const PAGE_SIZE = 15;

type Notice = { tone: 'success' | 'danger'; text: string };

export interface RaFindingsScreenProps {
  source: RaFindingSource;
  /** Sorgu dizesinden (`raFindingsRoute`): bulgu ya da doğrulamada yeni bulgu. */
  route?: RaFindingsRoute;
  /** Yazmadan sonra (gemide pano tazelenir). */
  onChanged?: () => void;
}

function useK() {
  const { t } = useTranslation();
  return {
    t,
    k: (key: string, options: Record<string, unknown> = {}) => t(`riskFindings.${key}`, options),
  };
}

/** Hata kodunun metni: bulgu metinleri, yoksa doğrulama ve risk metinleri. */
function useFindingError() {
  const { t } = useTranslation();
  return (error: unknown) => {
    const code = typeof error === 'string' ? error : errorCode(error);
    return (
      t(`riskFindings.errors.${code}`, { defaultValue: '' }) ||
      t(`riskVerification.errors.${code}`, { defaultValue: '' }) ||
      t(`risk.errors.${code}`, { defaultValue: '' }) ||
      `${t('riskFindings.errors.GENERIC')} (${code})`
    );
  };
}

function shipTime(iso: string | null | undefined, offsetMin: number): string {
  if (!iso) return '—';
  return `${formatDateTime(iso, offsetMin)} (${offsetLabel(offsetMin)})`;
}

/** Bulgunun maddesinin metni: ortak madde dilden, ötekiler checklist'ten. */
function useItemLabel() {
  const { t } = useTranslation();
  return (f: Pick<RaFindingSummary, 'itemKey' | 'itemText'>) => {
    if (!f.itemKey) return null;
    if (f.itemKey.startsWith('common:')) {
      return t(`riskVerification.common.${f.itemKey.slice('common:'.length)}`);
    }
    return f.itemText ?? f.itemKey;
  };
}

/** Bulgu olayının ekran işlemi (gerekçe hepsinde zorunlu). */
const EVENTS: readonly {
  kind: RaFindingEvent;
  can: 'escalate' | 'changeRequest' | 'close' | 'reopen' | 'cancel';
  key: string;
  danger?: boolean;
}[] = [
  { kind: 'closed', can: 'close', key: 'close' },
  { kind: 'escalated', can: 'escalate', key: 'escalate' },
  { kind: 'change_requested', can: 'changeRequest', key: 'changeRequest' },
  { kind: 'reopened', can: 'reopen', key: 'reopen' },
  { kind: 'cancelled', can: 'cancel', key: 'cancel', danger: true },
];

/**
 * Compliance ▸ Risk Assessment ▸ Findings & Actions (K6; ADR 0094; FD §10; SS §7). Operasyonel
 * doğrulamanın "No" cevapları kendiliğinden engelleyici bulgu açar; gemide elle de bildirilir.
 * Düzeltici faaliyet kanıtla tamamlanır, madde yeniden doğrulamada Evet cevaplanır, gerekirse DPA'ya
 * yükseltilir; bulgu kaptan ya da yetkili zabitçe kapatılır. HSE uygunsuzluğu ve PMS arızası
 * düğmeyle açılıp bağlanır. Ofiste bulgular salt okunur; DPA yükseltmeyi, kütüphanenin gözden
 * geçireni RA değişiklik talebini karara bağlar. Tasarım yok; dil Operational Verification.
 */
export function RaFindingsScreen({ source, route, onChanged }: RaFindingsScreenProps) {
  const { k } = useK();
  const message = useFindingError();
  const shell = useShell();
  const [state, setState] = useState<
    | { kind: 'loading' }
    | { kind: 'error'; code: string }
    | { kind: 'ready'; view: RaFindingListView }
  >({ kind: 'loading' });
  const [vesselId, setVesselId] = useState<string | undefined>(undefined);
  const [opened, setOpened] = useState<RaFindingDetail | null>(null);
  const [creating, setCreating] = useState<string | null>(
    route?.view === 'new' ? route.verificationId : null,
  );
  const [notice, setNotice] = useState<Notice | null>(null);

  const load = useCallback(async () => {
    try {
      setState({ kind: 'ready', view: await source.list(vesselId) });
    } catch (error) {
      setState({ kind: 'error', code: errorCode(error) });
    }
  }, [source, vesselId]);
  useEffect(() => {
    void load();
  }, [load]);

  const open = async (id: string) => {
    try {
      setNotice(null);
      setOpened(await source.detail(id));
    } catch (error) {
      setNotice({ tone: 'danger', text: message(error) });
    }
  };
  // Yoldan gelen bulgu açık gelir (doğrulamanın kartından)
  const initial = route?.view === 'detail' ? route.id : null;
  // biome-ignore lint/correctness/useExhaustiveDependencies: yalnız yoldaki kimlik değişince
  useEffect(() => {
    if (initial) void open(initial);
  }, [initial]);

  const view = state.kind === 'ready' ? state.view : null;
  const changed = (next: RaFindingDetail, text: string | null) => {
    setOpened(next);
    if (text) setNotice({ tone: 'success', text });
    void load();
    onChanged?.();
  };
  const back = () => {
    setOpened(null);
    if (route && route.view !== 'list') shell?.navigate(raFindingsPath({ view: 'list' }));
  };

  return (
    <div className={purchasing.screen}>
      <PageHeader
        title={k('title')}
        description={k('description')}
        icon={<IconFlag size={26} />}
        actions={
          !opened && source.create && view?.can.create && view.verifications.length > 0 ? (
            <Button
              leftSection={<IconPlus size={16} />}
              onClick={() => setCreating(view.verifications[0]?.id ?? null)}
            >
              {k('new')}
            </Button>
          ) : null
        }
      />
      <p className={master.note}>{source.scope === 'ship' ? k('shipNote') : k('officeNote')}</p>
      {notice ? (
        <p
          className={purchasing.notice}
          data-tone={notice.tone}
          role={notice.tone === 'danger' ? 'alert' : 'status'}
        >
          {notice.text}
        </p>
      ) : null}
      {state.kind === 'error' ? (
        <Panel>
          <LoadError title={k('empty.error')} code={state.code} onRetry={() => void load()} />
        </Panel>
      ) : !view ? (
        <Panel flush aria-label={k('title')}>
          <TableSkeleton columns={8} label={k('title')} />
        </Panel>
      ) : opened ? (
        <FindingDetail
          key={opened.id}
          detail={opened}
          source={source}
          onBack={back}
          onChanged={changed}
          onError={(error) => setNotice({ tone: 'danger', text: message(error) })}
        />
      ) : view.vesselId === null ? (
        <Panel>
          <EmptyState title={k('empty.title')} description={k('empty.noVessel')} />
        </Panel>
      ) : (
        <FindingList
          view={view}
          scope={source.scope}
          onOpen={(id) => void open(id)}
          onVessel={setVesselId}
        />
      )}
      {source.create && view && creating ? (
        <FindingDialog
          title={k('dialogs.form.newTitle')}
          confirmLabel={k('dialogs.form.create')}
          verifications={view.verifications}
          verificationId={creating}
          initial={null}
          onClose={() => setCreating(null)}
          onConfirm={async (input) => {
            try {
              const created = await (source.create as NonNullable<typeof source.create>)({
                verificationId: input.verificationId,
                finding: input.finding,
                actions: input.actions,
              });
              setCreating(null);
              changed(created, k('notice.created', { number: created.number }));
              return null;
            } catch (error) {
              return errorCode(error);
            }
          }}
        />
      ) : null}
    </div>
  );
}

/** Liste: özet kartları, süzgeç ve bulgular tablosu; ofiste gemi seçimi. */
export function FindingList({
  view,
  scope,
  onOpen,
  onVessel,
}: {
  view: RaFindingListView;
  scope: 'ship' | 'web';
  onOpen: (id: string) => void;
  onVessel?: (vesselId: string) => void;
}) {
  const { k } = useK();
  const [filter, setFilter] = useState<FindingListFilter>(EMPTY_FINDING_FILTER);
  const [page, setPage] = useState(1);
  const items = useMemo(() => filterFindings(view.findings, filter), [view.findings, filter]);
  const stats = findingStats(view.findings);
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = items.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  return (
    <>
      <div className={master.stats}>
        <StatTile
          icon={<IconFlag size={22} />}
          label={k('stats.open')}
          value={stats.open}
          variant="plain"
        />
        <StatTile
          icon={<IconHandStop size={22} />}
          label={k('stats.blocking')}
          value={stats.blocking}
          tone={stats.blocking > 0 ? 'danger' : 'default'}
          variant="plain"
        />
        <StatTile
          icon={<IconClockExclamation size={22} />}
          label={k('stats.overdue')}
          value={stats.overdue}
          tone={stats.overdue > 0 ? 'warning' : 'default'}
          variant="plain"
        />
        <StatTile
          icon={<IconBuildingFactory2 size={22} />}
          label={k('stats.company')}
          value={stats.company}
          variant="plain"
        />
      </div>
      <Panel flush aria-label={k('title')}>
        <div className={master.toolbar}>
          {scope === 'web' && onVessel ? (
            <Select
              className={master.filter}
              label={k('vessel')}
              data={view.vessels.map((v) => ({ value: v.id, label: v.name }))}
              value={view.vesselId}
              allowDeselect={false}
              onChange={(value) => {
                if (!value) return;
                onVessel(value);
                setPage(1);
              }}
            />
          ) : null}
          <TextInput
            className={master.search}
            leftSection={<IconSearch size={16} aria-hidden />}
            placeholder={k('search')}
            aria-label={k('search')}
            value={filter.query}
            onChange={(event) => {
              setFilter({ ...filter, query: event.currentTarget.value });
              setPage(1);
            }}
          />
          <Select
            className={master.filter}
            label={k('filter.state')}
            data={FINDING_FILTERS.map((s) => ({ value: s, label: k(`filter.states.${s}`) }))}
            value={filter.state}
            allowDeselect={false}
            onChange={(value) => {
              setFilter({ ...filter, state: (value ?? 'all') as FindingListFilter['state'] });
              setPage(1);
            }}
          />
        </div>
        {view.findings.length === 0 ? (
          <EmptyState
            title={k('empty.title')}
            description={scope === 'ship' ? k('empty.ship') : k('empty.office')}
          />
        ) : items.length === 0 ? (
          <NoMatch title={k('empty.noMatch')} onClear={() => setFilter(EMPTY_FINDING_FILTER)} />
        ) : (
          <>
            <section
              className={table.tableWrap}
              aria-label={k('tableLabel')}
              // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
              tabIndex={0}
            >
              <table className={`${table.table} ${purchasing.table}`}>
                <thead>
                  <tr>
                    <th scope="col">{k('columns.number')}</th>
                    <th scope="col">{k('columns.verification')}</th>
                    <th scope="col">{k('columns.type')}</th>
                    <th scope="col">{k('columns.description')}</th>
                    <th scope="col">{k('columns.responsible')}</th>
                    <th scope="col">{k('columns.actions')}</th>
                    <th scope="col">{k('columns.status')}</th>
                    <th scope="col">{k('columns.company')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((f) => (
                    <FindingRow key={f.id} f={f} onOpen={onOpen} />
                  ))}
                </tbody>
              </table>
            </section>
            <TablePager
              page={current}
              pages={pages}
              summary={k('showing', {
                from: (current - 1) * PAGE_SIZE + 1,
                to: Math.min(current * PAGE_SIZE, items.length),
                total: items.length,
              })}
              label={k('pagination')}
              onPage={setPage}
            />
          </>
        )}
      </Panel>
    </>
  );
}

function FindingRow({ f, onOpen }: { f: RaFindingSummary; onOpen: (id: string) => void }) {
  const { k } = useK();
  const item = useItemLabel()(f);
  const open = f.status !== 'closed' && f.status !== 'cancelled';
  return (
    <tr className={classes.row} data-attention={(open && f.blocking) || undefined}>
      <td>
        <button
          type="button"
          className={master.codeButton}
          aria-label={k('open', { number: f.number })}
          onClick={() => onOpen(f.id)}
        >
          {f.number}
        </button>
      </td>
      <td className={master.wrapCell}>
        <span className={classes.stack}>
          <span className={classes.nowrap}>{f.verificationNo}</span>
          <span className={master.sub}>{f.operation}</span>
        </span>
      </td>
      <td>{k(`types.${f.type}`)}</td>
      <td className={master.wrapCell}>
        <span className={classes.stack}>
          <span>{f.description}</span>
          {item ? <span className={master.sub}>{item}</span> : null}
        </span>
      </td>
      <td>
        <span className={classes.stack}>
          <span>{f.responsible ?? k('none')}</span>
          {f.dueOn ? (
            <span className={`${master.sub} ${classes.nowrap}`}>{formatDay(f.dueOn)}</span>
          ) : null}
        </span>
      </td>
      <td className={classes.nowrap}>
        {f.actionsTotal > 0
          ? k('actionsValue', { done: f.actionsDone, total: f.actionsTotal })
          : k('none')}
      </td>
      <td>
        <span className={classes.stack}>
          <StatusBadge tone={findingTone(f.status)} withIcon={false}>
            {k(`statuses.${f.status}`)}
          </StatusBadge>
          {open && f.blocking ? (
            <StatusBadge tone="danger" withIcon={false}>
              {k('blocking')}
            </StatusBadge>
          ) : null}
          {f.overdue ? (
            <StatusBadge tone="warning" withIcon={false}>
              {k('overdue')}
            </StatusBadge>
          ) : null}
        </span>
      </td>
      <td>
        <span className={classes.stack}>
          {f.escalation !== 'none' ? (
            <StatusBadge tone={requestTone(f.escalation)} withIcon={false}>
              {k(`requests.escalation.${f.escalation}`)}
            </StatusBadge>
          ) : null}
          {f.changeRequest !== 'none' ? (
            <StatusBadge tone={requestTone(f.changeRequest)} withIcon={false}>
              {k(`requests.change_request.${f.changeRequest}`)}
            </StatusBadge>
          ) : null}
          {f.escalation === 'none' && f.changeRequest === 'none' ? k('none') : null}
        </span>
      </td>
    </tr>
  );
}

function Banner({
  tone,
  children,
}: {
  tone: 'info' | 'warning' | 'danger' | 'success';
  children: ReactNode;
}) {
  return (
    <p className={classes.banner} data-tone={tone} role={tone === 'danger' ? 'alert' : 'status'}>
      {tone === 'danger' || tone === 'warning' ? <IconAlertTriangle size={16} aria-hidden /> : null}
      <span>{children}</span>
    </p>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className={classes.fieldLabel}>{label}</div>
      <div className={classes.fieldValue}>{value}</div>
    </div>
  );
}

/** Ayrıntı: başlık ve işlemler, kapanış eksikleri, bulgu, faaliyetler, yeniden doğrulama, bağlar, şirket ve geçmiş. */
export function FindingDetail({
  detail,
  source,
  onBack,
  onChanged,
  onError,
}: {
  detail: RaFindingDetail;
  source: RaFindingSource;
  onBack: () => void;
  onChanged: (next: RaFindingDetail, notice: string | null) => void;
  onError: (error: unknown) => void;
}) {
  const { k } = useK();
  const shell = useShell();
  const itemLabel = useItemLabel();
  const [event, setEvent] = useState<(typeof EVENTS)[number] | null>(null);
  const [editing, setEditing] = useState(false);
  const [hse, setHse] = useState(false);
  const [defect, setDefect] = useState(false);
  const [deciding, setDeciding] = useState<RaFindingDecisionKind | null>(null);
  const offset = detail.tzOffsetMin;
  const item = itemLabel(detail);
  const buttons = EVENTS.filter((e) => source.event !== undefined && detail.can[e.can] === true);
  const requests = detail.events.filter(
    (e) => e.kind === 'escalated' || e.kind === 'change_requested',
  );

  return (
    <>
      <Panel flush aria-label={`${detail.number} · ${k(`types.${detail.type}`)}`}>
        <div className={library.head}>
          <div className={library.headTitle}>
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconArrowLeft size={14} />}
              onClick={onBack}
              style={{ alignSelf: 'flex-start' }}
            >
              {k('detail.back')}
            </Button>
            <h2>
              {detail.number} · {k(`types.${detail.type}`)}
            </h2>
            <span className={library.meta}>
              <StatusBadge tone={findingTone(detail.status)} withIcon={false}>
                {k(`statuses.${detail.status}`)}
              </StatusBadge>
              {detail.blocking && detail.editable ? (
                <StatusBadge tone="danger" withIcon={false}>
                  {k('blocking')}
                </StatusBadge>
              ) : null}
              {detail.overdue ? (
                <StatusBadge tone="warning" withIcon={false}>
                  {k('overdue')}
                </StatusBadge>
              ) : null}
              <button
                type="button"
                className={master.codeButton}
                onClick={() =>
                  shell?.navigate(`${RA_VERIFICATION_PATH}?id=${detail.verification.id}`)
                }
              >
                {k('detail.verification', {
                  number: detail.verification.number,
                  operation: detail.verification.operation,
                })}
              </button>
              {source.scope === 'web' ? <span>{k('detail.readOnly')}</span> : null}
            </span>
          </div>
          <div className={classes.actions}>
            {detail.can.edit && source.update ? (
              <Button
                variant="default"
                leftSection={<IconPencil size={16} />}
                onClick={() => setEditing(true)}
              >
                {k('actions.edit')}
              </Button>
            ) : null}
            {detail.can.openHse && source.openHse ? (
              <Button variant="default" onClick={() => setHse(true)}>
                {k('actions.openHse')}
              </Button>
            ) : null}
            {detail.can.openDefect && source.openDefect ? (
              <Button variant="default" onClick={() => setDefect(true)}>
                {k('actions.openDefect')}
              </Button>
            ) : null}
            {buttons.map((e) => (
              <Button
                key={e.kind}
                variant={e.kind === 'closed' ? 'filled' : 'default'}
                {...(e.danger ? { color: 'red' } : {})}
                disabled={e.kind === 'closed' && detail.closeBlockers.length > 0}
                onClick={() => setEvent(e)}
              >
                {k(`actions.${e.key}`)}
              </Button>
            ))}
            {detail.can.decideEscalation && source.decide ? (
              <Button onClick={() => setDeciding('escalation')}>
                {k('actions.decideEscalation')}
              </Button>
            ) : null}
            {detail.can.decideChangeRequest && source.decide ? (
              <Button onClick={() => setDeciding('change_request')}>
                {k('actions.decideChangeRequest')}
              </Button>
            ) : null}
          </div>
        </div>
        <div className={classes.banners}>
          {detail.editable && detail.closeBlockers.length > 0 ? (
            <Banner tone="warning">
              {k('detail.closeBlockers', {
                list: detail.closeBlockers.map((b) => k(`blockers.${b}`)).join('; '),
              })}
            </Banner>
          ) : null}
          {detail.editable && detail.closeBlockers.length === 0 ? (
            <Banner tone="success">{k('detail.closable')}</Banner>
          ) : null}
          {detail.escalation === 'pending' ? (
            <Banner tone="info">{k('detail.escalationPending')}</Banner>
          ) : null}
          {detail.escalation === 'returned' && detail.editable ? (
            <Banner tone="danger">{k('detail.escalationReturned')}</Banner>
          ) : null}
          {/* Ofisin iadesi ya da talebin reddi (ADR 0116 Ek): kim, ne zaman, gerekçe */}
          <ReturnedBanner
            notice={detail.returnNotice}
            offsetMin={detail.tzOffsetMin}
            hint={detail.editable}
          />
        </div>
        <div className={library.body}>
          <section className={library.card} aria-label={k('detail.findingCard')}>
            <div className={library.cardHead}>
              <span>{k('detail.findingCard')}</span>
            </div>
            <div className={library.cardGrid}>
              <Field label={k('detail.type')} value={k(`types.${detail.type}`)} />
              <Field
                label={k('detail.source')}
                value={`${k(`sources.${detail.source}`)} · ${k('detail.round', { round: detail.round })}`}
              />
              {item ? <Field label={k('detail.item')} value={item} /> : null}
              <Field
                label={k('detail.blocking')}
                value={detail.blocking ? k('detail.blockingYes') : k('detail.blockingNo')}
              />
              <Field label={k('detail.description')} value={detail.description} />
              <Field
                label={k('detail.immediateAction')}
                value={detail.immediateAction ?? k('none')}
              />
              <Field label={k('detail.responsible')} value={detail.responsible ?? k('none')} />
              <Field
                label={k('detail.dueOn')}
                value={detail.dueOn ? formatDay(detail.dueOn) : k('none')}
              />
              <Field
                label={k('detail.createdLabel')}
                value={k('detail.created', {
                  name: detail.createdByName ?? k('none'),
                  time: shipTime(detail.createdAt, offset),
                })}
              />
            </div>
          </section>

          <section className={library.card} aria-label={k('detail.actionsCard')}>
            <div className={library.cardHead}>
              <span>{k('detail.actionsCard')}</span>
            </div>
            {detail.actions.length === 0 ? (
              <div className={library.cardBody}>
                <span className={classes.muted}>{k('detail.noActions')}</span>
              </div>
            ) : (
              <section
                className={table.tableWrap}
                aria-label={k('detail.actionsCard')}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                <table className={`${table.table} ${purchasing.table}`}>
                  <thead>
                    <tr>
                      <th scope="col">{k('detail.actionCols.description')}</th>
                      <th scope="col">{k('detail.actionCols.responsible')}</th>
                      <th scope="col">{k('detail.actionCols.dueOn')}</th>
                      <th scope="col">{k('detail.actionCols.status')}</th>
                      <th scope="col">{k('detail.actionCols.evidence')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.actions.map((a) => (
                      <tr key={a.id}>
                        <td className={master.wrapCell}>{a.description}</td>
                        <td>{a.responsible ?? k('none')}</td>
                        <td className={classes.nowrap}>
                          {a.dueOn ? formatDay(a.dueOn) : k('none')}
                        </td>
                        <td>
                          <span className={classes.stack}>
                            <span>{k(`dialogs.form.actionStatuses.${a.status}`)}</span>
                            {a.completedOn ? (
                              <span className={master.sub}>
                                {k('detail.completedOn', { day: formatDay(a.completedOn) })}
                              </span>
                            ) : null}
                            {a.cancelReason ? (
                              <span className={master.sub}>
                                {k('detail.cancelReason', { reason: a.cancelReason })}
                              </span>
                            ) : null}
                          </span>
                        </td>
                        <td className={master.wrapCell}>{a.evidence ?? k('none')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}
          </section>

          {source.attachments ? (
            <section className={library.card} aria-label={k('detail.filesCard')}>
              <div className={library.cardHead}>
                <span>{k('detail.filesCard')}</span>
              </div>
              <div className={library.cardBody}>
                {/* Kanıt dosyası yalnız açık bulguya, gemide (ADR 0121 §1; FINDING_LOCKED) */}
                <OwnerAttachments
                  ownerId={detail.id}
                  list={source.attachments}
                  upload={detail.editable && detail.can.edit ? source.upload : undefined}
                  href={source.attachmentUrl}
                  emptyText={k('detail.filesEmpty')}
                  addLabel={k('detail.filesAdd')}
                  label={k('detail.filesCard')}
                  onError={onError}
                />
              </div>
            </section>
          ) : null}

          <section className={library.card} aria-label={k('detail.reverificationCard')}>
            <div className={library.cardHead}>
              <span>{k('detail.reverificationCard')}</span>
            </div>
            <div className={library.cardBody}>
              <span className={classes.muted}>
                {detail.source === 'manual'
                  ? k('detail.reverificationManual')
                  : detail.reverification
                    ? k('detail.reverificationValue', {
                        round: detail.reverification.round,
                        answer: k(`detail.answers.${detail.reverification.answer}`),
                      })
                    : k('detail.reverificationNone')}
              </span>
            </div>
          </section>

          <section className={library.card} aria-label={k('detail.linksCard')}>
            <div className={library.cardHead}>
              <span>{k('detail.linksCard')}</span>
            </div>
            <div className={library.cardGrid}>
              <Field
                label={k('detail.hse')}
                value={
                  detail.hseReportId && detail.hseReportNo ? (
                    <button
                      type="button"
                      className={master.codeButton}
                      aria-label={k('detail.openRecord', { number: detail.hseReportNo })}
                      onClick={() =>
                        shell?.navigate(
                          hsePath('non_conformity', {
                            view: 'detail',
                            id: detail.hseReportId as string,
                            tab: null,
                          }),
                        )
                      }
                    >
                      {detail.hseReportNo}
                    </button>
                  ) : (
                    k('detail.notLinked')
                  )
                }
              />
              <Field
                label={k('detail.defect')}
                value={
                  detail.defectId && detail.defectNo ? (
                    <button
                      type="button"
                      className={master.codeButton}
                      aria-label={k('detail.openRecord', { number: detail.defectNo })}
                      onClick={() =>
                        shell?.navigate(
                          defectsPath({ view: 'detail', id: detail.defectId as string }),
                        )
                      }
                    >
                      {detail.defectNo}
                    </button>
                  ) : (
                    k('detail.notLinked')
                  )
                }
              />
            </div>
          </section>

          <section className={library.card} aria-label={k('detail.companyCard')}>
            <div className={library.cardHead}>
              <span>{k('detail.companyCard')}</span>
              <span className={library.meta}>
                <StatusBadge tone={requestTone(detail.escalation)} withIcon={false}>
                  {k(`requests.escalation.${detail.escalation}`)}
                </StatusBadge>
                <StatusBadge tone={requestTone(detail.changeRequest)} withIcon={false}>
                  {k(`requests.change_request.${detail.changeRequest}`)}
                </StatusBadge>
              </span>
            </div>
            {requests.length === 0 ? (
              <div className={library.cardBody}>
                <span className={classes.muted}>{k('detail.noRequests')}</span>
              </div>
            ) : (
              <ol className={classes.timeline}>
                {requests.map((r) => {
                  const decision = detail.decisions.find((d) => d.requestId === r.id);
                  return (
                    <li key={r.id}>
                      <span className={classes.when}>{shipTime(r.at, r.tzOffsetMin)}</span>
                      <span className={classes.what}>
                        <span>
                          <strong>{k(`events.${r.kind}`)}</strong>
                          {' · '}
                          {r.byName ?? k('none')}
                          {r.roleName ? ` (${r.roleName})` : ''}
                        </span>
                        <span className={classes.detailLine}>{r.reason}</span>
                        {decision ? (
                          <span className={classes.detailLine}>
                            <strong>
                              {k('detail.decision', {
                                decision: k(`decisions.${decision.decision}`),
                                name: decision.byName ?? k('none'),
                                role: decision.roleName ? ` (${decision.roleName})` : '',
                                time: shipTime(decision.at, 0),
                              })}
                            </strong>
                            {decision.comment ? ` — ${decision.comment}` : ''}
                          </span>
                        ) : null}
                      </span>
                    </li>
                  );
                })}
              </ol>
            )}
          </section>

          <section className={library.card} aria-label={k('detail.history')}>
            <div className={library.cardHead}>
              <span>{k('detail.history')}</span>
            </div>
            {detail.events.length === 0 ? (
              <div className={library.cardBody}>
                <span className={classes.muted}>{k('none')}</span>
              </div>
            ) : (
              <ol className={classes.timeline}>
                {detail.events.map((e) => (
                  <li key={e.id}>
                    <span className={classes.when}>{shipTime(e.at, e.tzOffsetMin)}</span>
                    <span className={classes.what}>
                      <span>
                        <strong>{k(`events.${e.kind}`)}</strong>
                        {' · '}
                        {e.byName ?? k('none')}
                        {e.roleName ? ` (${e.roleName})` : ''}
                      </span>
                      <span className={classes.detailLine}>{e.reason}</span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>
      </Panel>
      {event && source.event ? (
        <ActionDialog
          opened
          title={k(`dialogs.${event.key}.title`, { number: detail.number })}
          body={k(`dialogs.${event.key}.body`)}
          reason="required"
          password={false}
          reauthKind="ra_operation"
          confirmLabel={k(`actions.${event.key}`)}
          {...(event.danger ? { danger: true } : {})}
          messageOf={(code) =>
            k(`errors.${code}`, { defaultValue: '' }) || `${k('errors.GENERIC')} (${code})`
          }
          onClose={() => setEvent(null)}
          onConfirm={async (input) => {
            try {
              const next = await (source.event as NonNullable<typeof source.event>)(detail.id, {
                kind: event.kind,
                reason: input.reason,
              });
              setEvent(null);
              onChanged(
                next,
                k('notice.event', { number: next.number, event: k(`events.${event.kind}`) }),
              );
              return null;
            } catch (error) {
              return errorCode(error);
            }
          }}
        />
      ) : null}
      {hse && source.openHse ? (
        <ActionDialog
          opened
          title={k('dialogs.openHse.title')}
          body={k('dialogs.openHse.body')}
          reason="none"
          password={false}
          confirmLabel={k('dialogs.openHse.confirm')}
          messageOf={(code) =>
            k(`errors.${code}`, { defaultValue: '' }) || `${k('errors.GENERIC')} (${code})`
          }
          onClose={() => setHse(false)}
          onConfirm={async () => {
            try {
              const next = await (source.openHse as NonNullable<typeof source.openHse>)(detail.id);
              setHse(false);
              onChanged(next, k('notice.hse', { number: next.hseReportNo ?? '' }));
              return null;
            } catch (error) {
              return errorCode(error);
            }
          }}
        />
      ) : null}
      {defect && source.openDefect ? (
        <DefectDialog
          onClose={() => setDefect(false)}
          onConfirm={async (input) => {
            try {
              const next = await (source.openDefect as NonNullable<typeof source.openDefect>)(
                detail.id,
                input,
              );
              setDefect(false);
              onChanged(next, k('notice.defect', { number: next.defectNo ?? '' }));
              return null;
            } catch (error) {
              return errorCode(error);
            }
          }}
        />
      ) : null}
      {deciding && source.decide ? (
        <DecisionDialog
          kind={deciding}
          detail={detail}
          onClose={() => setDeciding(null)}
          onConfirm={async (input) => {
            try {
              const next = await (source.decide as NonNullable<typeof source.decide>)(
                detail.id,
                input,
              );
              setDeciding(null);
              onChanged(next, k('notice.decided'));
              return null;
            } catch (error) {
              return errorCode(error);
            }
          }}
        />
      ) : null}
      {editing && source.update ? (
        <FindingDialog
          title={k('dialogs.form.editTitle', { number: detail.number })}
          confirmLabel={k('dialogs.form.save')}
          verifications={[]}
          verificationId={detail.verificationId}
          initial={detail}
          onClose={() => setEditing(false)}
          onConfirm={async (input) => {
            try {
              const next = await (source.update as NonNullable<typeof source.update>)(
                detail.id,
                detail.rowVersion,
                input.finding,
                input.actions,
              );
              setEditing(false);
              onChanged(next, k('notice.saved'));
              return null;
            } catch (error) {
              return errorCode(error);
            }
          }}
        />
      ) : null}
    </>
  );
}

function DialogError({ code }: { code: string | null }) {
  const message = useFindingError();
  if (!code) return null;
  return (
    <p className={classes.invalid} role="alert">
      {message(code)}
    </p>
  );
}

/** Yeni ya da düzenlenen bulgu: alanlar ve düzeltici faaliyetler. */
function FindingDialog({
  title,
  confirmLabel,
  verifications,
  verificationId,
  initial,
  onClose,
  onConfirm,
}: {
  title: string;
  confirmLabel: string;
  verifications: RaFindingListView['verifications'];
  verificationId: string;
  initial: RaFindingDetail | null;
  onClose: () => void;
  onConfirm: (input: {
    verificationId: string;
    finding: RaFindingFields;
    actions: RaFindingActionInput[];
  }) => Promise<string | null>;
}) {
  const { k, t } = useK();
  const [verification, setVerification] = useState(verificationId);
  const [fields, setFields] = useState<RaFindingFields>(() => ({
    type: initial?.type ?? 'operational_constraint',
    description: initial?.description ?? '',
    blocking: initial?.blocking ?? true,
    immediateAction: initial?.immediateAction ?? null,
    responsible: initial?.responsible ?? null,
    dueOn: initial?.dueOn ?? null,
  }));
  const [actions, setActions] = useState<RaFindingActionInput[]>(() =>
    (initial?.actions ?? []).map(actionDraft),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fixedBlocking = initial?.source === 'failed_check';
  const invalid =
    fields.description.trim() === '' || actions.some((a) => actionIssues(a).length > 0);
  const set = (patch: Partial<RaFindingFields>) => setFields({ ...fields, ...patch });
  const setAction = (index: number, patch: Partial<RaFindingActionInput>) =>
    setActions(actions.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  const text = (v: string) => (v.trim() === '' ? null : v);
  return (
    <Modal
      opened
      onClose={onClose}
      title={title}
      size="xl"
      closeButtonProps={{ 'aria-label': t('risk.dialog.cancel') }}
    >
      <div className={master.form}>
        {initial === null && verifications.length > 0 ? (
          <Select
            label={k('dialogs.form.verification')}
            data={verifications.map((v) => ({
              value: v.id,
              label: `${v.number} · ${v.operation}`,
            }))}
            value={verification}
            allowDeselect={false}
            onChange={(value) => value && setVerification(value)}
          />
        ) : null}
        <Select
          label={k('dialogs.form.type')}
          data={RA_FINDING_TYPES.map((t) => ({ value: t, label: k(`types.${t}`) }))}
          value={fields.type}
          allowDeselect={false}
          onChange={(value) => value && set({ type: value as RaFindingType })}
        />
        <Textarea
          label={k('dialogs.form.description')}
          description={k('dialogs.form.descriptionHint')}
          required
          autosize
          minRows={2}
          value={fields.description}
          error={fields.description.trim() === '' ? ' ' : undefined}
          onChange={(e) => set({ description: e.currentTarget.value })}
        />
        <Checkbox
          label={k('dialogs.form.blocking')}
          description={fixedBlocking ? k('dialogs.form.blockingFixed') : undefined}
          checked={fields.blocking}
          disabled={fixedBlocking}
          onChange={(e) => set({ blocking: e.currentTarget.checked })}
        />
        <Textarea
          label={k('dialogs.form.immediateAction')}
          description={k('dialogs.form.immediateActionHint')}
          autosize
          minRows={2}
          value={fields.immediateAction ?? ''}
          onChange={(e) => set({ immediateAction: text(e.currentTarget.value) })}
        />
        <div className={classes.pair}>
          <TextInput
            label={k('dialogs.form.responsible')}
            value={fields.responsible ?? ''}
            onChange={(e) => set({ responsible: text(e.currentTarget.value) })}
          />
          <DayInput
            label={k('dialogs.form.dueOn')}
            value={fields.dueOn}
            clearable
            onChange={(value) => set({ dueOn: value })}
          />
        </div>
        <h3 className={classes.groupTitle}>{k('dialogs.form.actions')}</h3>
        {actions.map((a, index) => {
          const issues = actionIssues(a);
          return (
            <fieldset key={a.id ?? `new-${index}`} className={master.form}>
              <legend className={classes.fieldLabel}>
                {k('dialogs.form.action.description')} {index + 1}
              </legend>
              <Textarea
                label={k('dialogs.form.action.description')}
                required
                autosize
                minRows={1}
                value={a.description}
                error={issues.includes('ACTION_DESCRIPTION_REQUIRED') ? ' ' : undefined}
                onChange={(e) => setAction(index, { description: e.currentTarget.value })}
              />
              <div className={classes.pair}>
                <TextInput
                  label={k('dialogs.form.action.responsible')}
                  value={a.responsible ?? ''}
                  onChange={(e) => setAction(index, { responsible: text(e.currentTarget.value) })}
                />
                <DayInput
                  label={k('dialogs.form.action.dueOn')}
                  value={a.dueOn}
                  clearable
                  onChange={(value) => setAction(index, { dueOn: value })}
                />
              </div>
              <div className={classes.pair}>
                <Select
                  label={k('dialogs.form.action.status')}
                  data={RA_FINDING_ACTION_STATUSES.map((s) => ({
                    value: s,
                    label: k(`dialogs.form.actionStatuses.${s}`),
                  }))}
                  value={a.status}
                  allowDeselect={false}
                  onChange={(value) =>
                    value &&
                    setAction(index, {
                      status: value as RaFindingActionInput['status'],
                      ...(value === 'completed' ? {} : { completedOn: null }),
                    })
                  }
                />
                {a.status === 'completed' ? (
                  <DayInput
                    label={k('dialogs.form.action.completedOn')}
                    value={a.completedOn}
                    required
                    error={issues.includes('COMPLETED_DATE_REQUIRED') ? ' ' : undefined}
                    onChange={(value) => setAction(index, { completedOn: value })}
                  />
                ) : null}
              </div>
              <TextInput
                label={k('dialogs.form.action.evidence')}
                description={k('dialogs.form.action.evidenceHint')}
                required={a.status === 'completed'}
                value={a.evidence ?? ''}
                error={issues.includes('EVIDENCE_REQUIRED') ? ' ' : undefined}
                onChange={(e) => setAction(index, { evidence: text(e.currentTarget.value) })}
              />
              {a.status === 'cancelled' ? (
                <TextInput
                  label={k('dialogs.form.action.cancelReason')}
                  required
                  value={a.cancelReason ?? ''}
                  error={issues.includes('CANCEL_REASON_REQUIRED') ? ' ' : undefined}
                  onChange={(e) => setAction(index, { cancelReason: text(e.currentTarget.value) })}
                />
              ) : null}
              {a.id ? null : (
                <Button
                  variant="subtle"
                  color="red"
                  size="compact-sm"
                  leftSection={<IconTrash size={14} />}
                  style={{ alignSelf: 'flex-start' }}
                  onClick={() => setActions(actions.filter((_, i) => i !== index))}
                >
                  {k('dialogs.form.removeAction')}
                </Button>
              )}
            </fieldset>
          );
        })}
        <Button
          variant="default"
          leftSection={<IconPlus size={16} />}
          style={{ alignSelf: 'flex-start' }}
          disabled={actions.length >= 20}
          onClick={() => setActions([...actions, { ...EMPTY_ACTION }])}
        >
          {k('dialogs.form.addAction')}
        </Button>
        {invalid ? <span className={classes.muted}>{k('dialogs.form.invalid')}</span> : null}
        <DialogError code={error} />
        <div className={classes.saveRow}>
          <Button variant="default" onClick={onClose}>
            {t('risk.dialog.cancel')}
          </Button>
          <Button
            loading={busy}
            disabled={invalid}
            onClick={async () => {
              setBusy(true);
              setError(
                await onConfirm({
                  verificationId: verification,
                  finding: { ...fields, description: fields.description.trim() },
                  actions,
                }),
              );
              setBusy(false);
            }}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Bulgudan PMS arızası: ekipman/sistem adı ve ciddiyet. */
function DefectDialog({
  onClose,
  onConfirm,
}: {
  onClose: () => void;
  onConfirm: (input: {
    systemName: string;
    severity: 'minor' | 'major' | 'critical';
  }) => Promise<string | null>;
}) {
  const { k, t } = useK();
  const [systemName, setSystemName] = useState('');
  const [severity, setSeverity] = useState<'minor' | 'major' | 'critical'>('major');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      opened
      onClose={onClose}
      title={k('dialogs.defect.title')}
      closeButtonProps={{ 'aria-label': t('risk.dialog.cancel') }}
    >
      <div className={master.form}>
        <p className={classes.muted}>{k('dialogs.defect.body')}</p>
        <TextInput
          label={k('dialogs.defect.systemName')}
          required
          value={systemName}
          onChange={(e) => setSystemName(e.currentTarget.value)}
        />
        <Radio.Group
          label={k('dialogs.defect.severity')}
          value={severity}
          onChange={(value) => setSeverity(value as typeof severity)}
        >
          <div className={classes.radios}>
            {(['minor', 'major', 'critical'] as const).map((s) => (
              <Radio key={s} value={s} label={k(`dialogs.defect.severities.${s}`)} />
            ))}
          </div>
        </Radio.Group>
        <DialogError code={error} />
        <div className={classes.saveRow}>
          <Button variant="default" onClick={onClose}>
            {t('risk.dialog.cancel')}
          </Button>
          <Button
            loading={busy}
            disabled={systemName.trim() === ''}
            onClick={async () => {
              setBusy(true);
              setError(await onConfirm({ systemName: systemName.trim(), severity }));
              setBusy(false);
            }}
          >
            {k('dialogs.defect.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Ofisin kararı: talebin metni, karar ve not (geri gönderme ve retle zorunlu). */
function DecisionDialog({
  kind,
  detail,
  onClose,
  onConfirm,
}: {
  kind: RaFindingDecisionKind;
  detail: RaFindingDetail;
  onClose: () => void;
  onConfirm: (input: {
    kind: RaFindingDecisionKind;
    requestId: string;
    decision: RaFindingDecision;
    comment: string | null;
  }) => Promise<string | null>;
}) {
  const { k, t } = useK();
  const options: RaFindingDecision[] =
    kind === 'escalation' ? ['approved', 'returned'] : ['accepted', 'rejected'];
  const requestId =
    (kind === 'escalation' ? detail.pendingEscalation : detail.pendingChangeRequest) ?? '';
  const request = detail.events.find((e) => e.id === requestId);
  const [decision, setDecision] = useState<RaFindingDecision>(options[0] as RaFindingDecision);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const needsComment = decision === 'returned' || decision === 'rejected';
  return (
    <Modal
      opened
      onClose={onClose}
      closeButtonProps={{ 'aria-label': t('risk.dialog.cancel') }}
      title={k(
        kind === 'escalation' ? 'dialogs.decide.escalationTitle' : 'dialogs.decide.changeTitle',
        {
          number: detail.number,
        },
      )}
    >
      <div className={master.form}>
        {request ? (
          <Field
            label={k('dialogs.decide.request')}
            value={`${request.reason} — ${request.byName ?? ''}`}
          />
        ) : null}
        <Radio.Group
          label={k('dialogs.decide.decision')}
          value={decision}
          onChange={(value) => setDecision(value as RaFindingDecision)}
        >
          <div className={classes.radios}>
            {options.map((o) => (
              <Radio key={o} value={o} label={k(`decisions.${o}`)} />
            ))}
          </div>
        </Radio.Group>
        <Textarea
          label={k('dialogs.decide.comment')}
          description={k('dialogs.decide.commentHint')}
          required={needsComment}
          autosize
          minRows={2}
          value={comment}
          onChange={(e) => setComment(e.currentTarget.value)}
        />
        <DialogError code={error} />
        <div className={classes.saveRow}>
          <Button variant="default" onClick={onClose}>
            {t('risk.dialog.cancel')}
          </Button>
          <Button
            loading={busy}
            disabled={requestId === '' || (needsComment && comment.trim().length < 5)}
            onClick={async () => {
              setBusy(true);
              setError(
                await onConfirm({
                  kind,
                  requestId,
                  decision,
                  comment: comment.trim() === '' ? null : comment.trim(),
                }),
              );
              setBusy(false);
            }}
          >
            {k('dialogs.decide.confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
